import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatCheckbox} from '@angular/material/checkbox';
import {UserDateType} from '@app/shared/enums/userStatus.enum';
import {DateTypeFilterFormValue} from "@app/shared/interfaces/dateTypeFilterFormValue.interface";

const defaultFormValue: DateTypeFilterFormValue = {
  dateType: null,
  startDate: '',
  endDate: ''
};

@Component({
  selector: 'app-date-type-filter',
  imports: [MatCheckbox, ReactiveFormsModule],
  templateUrl: './date-type-filter.component.html',
  styleUrl: './date-type-filter.component.scss'
})
export class DateTypeFilterComponent {
  @Output() readonly formChanged = new EventEmitter<DateTypeFilterFormValue>();

  readonly dateTypes = [
    { label: 'Registration date', value: UserDateType.REGISTRATION },
    { label: 'Last login date', value: UserDateType.LAST_LOGIN }
  ] as const;

  readonly form: FormGroup<{
    dateType: FormControl<UserDateType | null>;
    startDate: FormControl<string | null>;
    endDate: FormControl<string | null>;
  }>;

  // Allowing the input to be undefined is easier to deal with to keep checkboxes exclusive or and clearing filters.
  @Input()
  set formValue(value: DateTypeFilterFormValue | undefined) {
    this.form.setValue(value ?? defaultFormValue, { emitEvent: false });
  }

  constructor() {
    this.form = new FormGroup({
      dateType: new FormControl<UserDateType | null>(null),
      startDate: new FormControl(''),
      endDate: new FormControl('')
    });

    this.form.controls.startDate.valueChanges.subscribe(() => this.onDateRangeChange());
    this.form.controls.endDate.valueChanges.subscribe(() => this.onDateRangeChange());
  }

  get hasDateRange(): boolean {
    const { startDate, endDate } = this.form.getRawValue();
    return Boolean(startDate || endDate);
  }

  onDateTypeChange(dateType: UserDateType, checked: boolean): void {
    this.form.controls.dateType.setValue(checked ? dateType : null);

    if (!checked) {
      this.form.setValue(defaultFormValue, { emitEvent: false });
      this.emitFormChanged();
    } else if (this.hasDateRange) {
      this.emitFormChanged();
    }
  }

  private onDateRangeChange(): void {
    if (!this.hasDateRange) {
      this.form.controls.dateType.setValue(null, { emitEvent: false });
    }
    this.emitFormChanged();
  }

  private emitFormChanged(): void {
    this.formChanged.emit(this.form.getRawValue());
  }
}
